import React, { useState, useEffect } from 'react';
import { Modal, Form, Checkbox, Select, Button, Alert } from 'antd';
import { IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import PropTypes from 'prop-types';
import './SkillsForm.css';
import { getSkillCertList } from '../../../../services/Dashboard';
import { debounce } from 'lodash';

const SKILL_LEVELS = {
  1: 'Beginner',
  2: 'Experienced',
  3: 'Advanced',
  4: 'Expert'
};

const levelMapping = {
  beginner: 'I do not have any professional experience in this skill.',
  experienced: 'I have used this skill professionally in a limited capacity.',
  advanced: ' I have used this skill professionally at least once a week (example: deployment).',
  expert: 'I have used this skill professionally on a daily basis.'
};

const levelList = Object.entries(SKILL_LEVELS).map(([level_id, level]) => {
  return (
    <Select.Option key={level_id} value={+level_id} label={level}>
      <div>
        <div>
          <b>{level}</b>
        </div>
        <div style={{ color: '#888888', whiteSpace: 'normal' }}>
          {levelMapping[level.toLowerCase()]}
        </div>
      </div>
    </Select.Option>
  );
});

function searchSkills (value, callback) {
  if (!value /* || `${value}`.length < 3 */) {
    // eslint-disable-next-line standard/no-callback-literal
    return callback && callback([]);
  } else {
    getSkillCertList({
      action: 'GET_SKILLS_CERTIFICATION_LIST',
      field: 'skills',
      value: value,
      searchId: null
    })
      .then(response => response.data)
      .then(({ success, result }) => success ? callback(result.map(item => ({ ...item, skill_id: item.id, id: undefined }))) : null);
  }
}
const debouncedSearchSkills = debounce(searchSkills, 1000);

const SkillsForm = React.forwardRef((props, ref) => {
  const { userSkills = [], parsedSkills = [], /* value, */ onChange } = props;
  const [skills, setSkills] = useState([...userSkills.map(item => ({ ...item }))]);
  let [state, setState] = useState({
    showMessage: false,
    skills: []
  });
  let [selectedSkill, setSelectedSkill] = useState({
    skill_id: null,
    score: null,
    skill_level: null,
    preferred: null
  });

  state.set = (obj = {}) => {
    state = { ...state, ...obj };
    setState(state);
  };

  selectedSkill.set = (obj = {}) => {
    selectedSkill = { ...selectedSkill, ...obj };
    setSelectedSkill(selectedSkill);
  };

  selectedSkill.bind = (name) => {
    return {
      value: selectedSkill[name] ? selectedSkill[name] : undefined,
      onChange: (val) => selectedSkill.set({ [name]: parseInt(val) }),
      showSearch: true,
      filterOption: (input, option) => {
        let value = '';
        if (typeof option.props.children === 'string') {
          value = option.props.children;
        } else if (typeof option.props.label === 'string') {
          value = option.props.label;
        } else if ('value' in option.props) {
          value = `${option.props.value}`;
        }
        return value.toLowerCase().indexOf(input.toLowerCase()) >= 0;
      },
      optionLabelProp: 'label'
    };
  };

  const initSkillItem = (item, index) => {
    item.savedItem = (userSkills || []).find(
      (elm) => elm.skill_id === item.skill_id
    );
    item.checkedByDefault = !!item.savedItem;
    item.checked = !!item.savedItem;
    item.savedId = item.savedItem && item.savedItem.id;
    if (item.checked) {
      item.index = index;
    } else {
      item.index = -1;
    }
    item.score = item.savedItem && item.savedItem.score;
    item.skill_level = item.savedItem && item.savedItem.skill_level;
    item.preferred = item.savedItem && item.savedItem.preferred;
    return item;
  };

  useEffect(() => {
    let index = 0;
    let list = skills.map(initSkillItem);
    if (list.filter(item => item.checked).length === 0) {
      list = skills.map((item) => {
        const parsedItem = (parsedSkills || []).find(
          (elm) => elm.skill_id === item.skill_id
        );
        item.savedItem = null;
        item.checkedByDefault = false;
        item.checked = !!parsedItem;
        item.savedId = null;
        if (item.checked) {
          item.index = index;
          index++;
          state.set({ showMessage: true });
        } else {
          item.index = -1;
        }
        item.score = (parsedItem && parsedItem.score) || 1;
        item.skill_level = (parsedItem && parsedItem.skill_level) || 1;
        item.preferred = false;
        return item;
      });
      handleChange();
    }
    list.sort((a, b) => {
      if (a.index < b.index || a.savedId < b.savedId) return -1;
      if (a.index > b.index || a.savedId > b.savedId) return 1;
      return 0;
    });
    setSkills(list);
  }, [userSkills, parsedSkills]);

  const checkIfChanged = (skill) => {
    if (!skill.savedItem) return false;
    if (skill.score !== skill.savedItem.score) return true;
    if (skill.skill_level !== skill.savedItem.skill_level) return true;
    if (!!skill.preferred !== !!skill.savedItem.preferred) return true;
    return false;
  };

  const handleChange = () => {
    const result = {
      addSkills: skills
        .filter((skill) => skill.checked && !skill.checkedByDefault)
        .map((skill) => ({
          skill_id: skill.skill_id,
          score: skill.score || null,
          skill_level: skill.skill_level || null,
          preferred: !!skill.preferred
        })),
      deleteSkills: skills
        .filter((skill) => !skill.checked && skill.checkedByDefault)
        .map((skill) => skill.savedItem.id),
      updateSkills: skills
        .filter((skill) => skill.checked && checkIfChanged(skill))
        .map((skill) => ({
          id: skill.savedItem.id,
          skill_id: skill.skill_id,
          score: skill.score || null,
          skill_level: skill.skill_level || null,
          preferred: !!skill.preferred
        }))
    };
    if (
      result.addSkills.length === 0 &&
      result.deleteSkills.length === 0 &&
      result.updateSkills.length === 0
    ) {
      if (onChange) onChange(false);
      return;
    }
    if (onChange) onChange(result);
  };

  const addSkill = () => {
    const skillEdit = skills.find(
      ({ skill_id }) => selectedSkill.skill_id === skill_id
    );
    if (skillEdit) {
      skillEdit.score = selectedSkill.score;
      skillEdit.skill_level = selectedSkill.skill_level;
      skillEdit.preferred = selectedSkill.preferred;
      skillEdit.checked = true;
    } else {
      const skillToAdd = state.skills.find(
        ({ skill_id }) => selectedSkill.skill_id === skill_id
      );
      if (skillToAdd) {
        skills.push({
          ...selectedSkill,
          skill_name: skillToAdd.skill_name,
          index: skills.length,
          checked: true,
          preferred: false
        });
      }
    }
    skills.sort((a, b) => {
      if (a.index < b.index || a.savedId < b.savedId) return -1;
      if (a.index > b.index || a.savedId > b.savedId) return 1;
      return 0;
    });
    handleChange();
    setSkills([...skills]);
    closeAddEditModal();
  };

  const closeAddEditModal = () => {
    selectedSkill.set({
      skill_id: null,
      score: 0,
      skill_level: null,
      preferred: false,
      editing: false,
      adding: false
    });
  };

  skills.forEach((skill) => {
    skill.set = (obj = {}) => {
      Object.entries(obj).forEach(([key, value]) => {
        skill[key] = value;
      });
      setSkills([...skills]);
      handleChange();
    };
    skill.onEdit = () => {
      setSelectedSkill({ ...skill, editing: true });
    };
    skill.onDelete = () => {
      skill.set({ checked: false });
    };
  });

  const handleSearch = (value) => {
    /* 27th SEPT. IT'S GOOD TO HAVE cancel() BECAUSE THE PREVIOUS REQUEST MAY TAKE LONG TIME TO FINISH
    I.E FLOW: TYPE SOMETHING -> WAIT 1s -> REQUEST (TAKE 10s) -> TYPE MORE -> REQUEST -> SO THE 10s REQUEST WILL BE CANCEL */
    if (debouncedSearchSkills) debouncedSearchSkills.cancel();
    debouncedSearchSkills(value, skills => setState({ ...state, skills: skills.map(initSkillItem) }));
  };

  return (
    <>
      { state.showMessage ? <Alert message="We have added the list of skills after parsing your resume, please check and save the list." /> : null }
      <table className="skills-table">
        <thead>
          <tr>
            <th>Skill</th>
            <th>Competency</th>
            <th>Years of Professional Experience</th>
            <th>Preferred</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {skills
            .filter((skill) => skill.checked)
            .map((skill) => (
              <SkillsFormItem key={skill.skill_id} {...skill} />
            ))}
        </tbody>
      </table>
      <Button
        style={{ marginTop: 20 }}
        onClick={(e) => setSelectedSkill({ adding: true })}
      >
        Add Skills
      </Button>
      <Modal
        visible={selectedSkill.editing || selectedSkill.adding}
        title={selectedSkill.editing ? 'Edit Skill' : 'Add Skill'}
        onOk={addSkill}
        okButtonProps={{ disabled: !selectedSkill.skill_id || !selectedSkill.skill_level || !selectedSkill.score }}
        onCancel={closeAddEditModal}
        width={350}
      >
        <Form.Item label="Skill">
          <Select
            style={{ width: '100%', minWidth: 200 }}
            {...selectedSkill.bind('skill_id')}
            disabled={selectedSkill.editing}
            placeholder="Enter skill name to search"
            onSearch={handleSearch}
            filterOption={false}
          >
            {selectedSkill.editing ? (
              <Select.Option
                value={+selectedSkill.skill_id}
                label={selectedSkill.skill_name}
                key={`${selectedSkill.skill_id}-${selectedSkill.skill_name}`}
              >
                {selectedSkill.skill_name}
              </Select.Option>
            ) : state.skills.map((skill) => (
              <Select.Option
                value={+skill.skill_id}
                label={skill.skill_name}
                key={`${skill.skill_id}-${skill.skill_name}`}
              >
                {skill.skill_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Competency">
          <Select
            style={{ width: '100%', minWidth: 200 }}
            {...selectedSkill.bind('skill_level')}
            placeholder="Select the level"
          >
            {levelList}
          </Select>
        </Form.Item>
        <Form.Item label="Years of Professional Experience">
          <Select
            style={{ width: '100%', minWidth: 200 }}
            {...selectedSkill.bind('score')}
            placeholder="Select an option"
          >
            {[...new Array(50)].map((v, i) => (
              <Select.Option value={i + 1} label={i + 1} key={i}>
                {i + 1}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Modal>
    </>
  );
});

const SkillsFormItem = React.memo(
  ({ skill_name, score, skill_level, preferred, set, onEdit, onDelete }) => {
    return (
      <tr>
        <td>{skill_name}</td>
        <td>{SKILL_LEVELS[skill_level]}</td>
        <td style={{ textAlign: 'center' }}>{score}</td>
        <td style={{ textAlign: 'center' }}>
          <Checkbox
            checked={!!preferred}
            onChange={(e) => set({ preferred: e.target.checked })}
          />
        </td>
        <td>
          <div className="skills-table-actions">
            <IconButton onClick={onEdit}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={onDelete}>
              <DeleteIcon />
            </IconButton>
          </div>
        </td>
      </tr>
    );
  }
);

SkillsForm.propTypes = {
  userSkills: PropTypes.array,
  parsedSkills: PropTypes.array,
  onChange: PropTypes.func
};

SkillsFormItem.propTypes = {
  skill_name: PropTypes.any,
  skill_level: PropTypes.any,
  score: PropTypes.any,
  preferred: PropTypes.any,
  set: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func
};

export default SkillsForm;
